import React, { lazy, Suspense, useEffect } from 'react';
import cn from 'classnames';
import Modal from 'components/UI/Modal';
import CloseButton from 'components/UI/CloseButton';
import Title from 'components/UI/Title';
import Loader from 'components/UI/Loader';
import ModalAside from 'components/ModalAside';
import TagCheckboxGroup from 'components/TagCheckboxGroup';
import { useOpenModalEventHandler } from 'components/Modals';
import { observer, tagsStore as store } from 'store';
import { appendScriptSrc } from 'utils/scripts';
import { recaptchSrc } from 'define/scripts';
import { useTrans, useLang } from 'utils/localisation';

import * as styles from './RequestServicesModal.module.scss';

const CorporateForm = lazy(() => import('components/Forms/CorporateForm/CorporateForm'));

function RequestServicesModal({
  animation = 'show',
  isOpen,
  closeModalCallback,
  title,
  services,
  clearTagsOnClose = true,
  id = 'request-service',
  formName = 'request-service',
}) {
  const lang = useLang();
  const shouldShowAside = lang !== 'ae';
  const t = useTrans(['common', 'corporateForm']);

  useOpenModalEventHandler(isOpen, 'request_services');

  useEffect(() => {
    const scriptLoaded = document.getElementById('recaptcha');

    if (isOpen && !scriptLoaded) {
      appendScriptSrc(recaptchSrc, 'body', 'recaptcha');
    }
  }, [isOpen]);

  useEffect(() => {
    return () => store.destroyTagsData(id);
  }, []);

  const clearTags = () => {
    store.updateTags(id, []);
  };

  const closeModal = () => {
    if (clearTagsOnClose) {
      clearTags();
    }
    closeModalCallback();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModalCallback={closeModal}
      closeButton={false}
      containerMod={shouldShowAside ? 'withAside' : null}
      contentMod="noPadding"
      animation={animation}
    >
      {onClose => (
        <div className={styles.container}>
          <div className={cn(styles.formWrap, !shouldShowAside && styles.withoutAside)}>
            <CloseButton onClose={onClose} />
            <Title tag="h3" className={styles.title}>
              {title}
            </Title>
            <TagCheckboxGroup storeId={id} tags={services} />
            <Suspense
              fallback={
                <div className={styles.loaderWrap}>
                  <Loader theme="fullHeight" />
                </div>
              }
            >
              <CorporateForm
                id={id}
                formName={formName}
                submitTitle={t('getQuote')}
                onSuccess={clearTags}
                getAdditionalFormData={() => ({
                  requestedServices: [...store.getTags(id)],
                })}
                closeModal={onClose}
                ndaPositionTop={true}
                inModal={true}
                theme="services"
              />
            </Suspense>
          </div>
          {shouldShowAside && <ModalAside className={styles.mobileHidden} />}
        </div>
      )}
    </Modal>
  );
}

export default observer(RequestServicesModal);
